import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, CheckCircle, RotateCcw, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const CircuitBreakerStatus = ({ workflowId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [workflowId]);

  const fetchStatus = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/workflows/${workflowId}/circuit-breaker`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch circuit breaker status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the circuit breaker? This will allow the workflow to run again.')) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_URL}/api/workflows/${workflowId}/circuit-breaker/reset`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Circuit breaker reset successfully!');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to reset circuit breaker');
      console.error('Reset error:', error);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading circuit breaker status...</div>;
  }

  if (!status) {
    return null;
  }

  const getStateColor = (state) => {
    switch (state) {
      case 'closed': return 'bg-green-100 text-green-700 border-green-300';
      case 'open': return 'bg-red-100 text-red-700 border-red-300';
      case 'half_open': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStateIcon = (state) => {
    switch (state) {
      case 'closed': return <CheckCircle className="w-5 h-5" />;
      case 'open': return <AlertTriangle className="w-5 h-5" />;
      case 'half_open': return <Activity className="w-5 h-5" />;
      default: return <Shield className="w-5 h-5" />;
    }
  };

  return (
    <Card className={`border-2 ${getStateColor(status.state)}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Circuit Breaker
        </CardTitle>
        <CardDescription>
          Automatic protection against cascading failures
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* State Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStateIcon(status.state)}
            <span className="font-semibold capitalize">{status.state.replace('_', ' ')}</span>
          </div>
          <Badge variant={status.can_execute ? 'success' : 'destructive'}>
            {status.can_execute ? 'Ready' : 'Blocked'}
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-muted-foreground">Failures</p>
            <p className="text-2xl font-bold text-red-600">{status.failure_count}</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-muted-foreground">Successes</p>
            <p className="text-2xl font-bold text-green-600">{status.success_count}</p>
          </div>
        </div>

        {/* Failure Rate */}
        {status.failure_rate !== undefined && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Failure Rate (Recent Window)</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-red-500 transition-all"
                  style={{ width: `${status.failure_rate * 100}%` }}
                />
              </div>
              <span className="text-sm font-semibold">
                {Math.round(status.failure_rate * 100)}%
              </span>
            </div>
          </div>
        )}

        {/* Last Failure */}
        {status.last_failure_time && (
          <div className="text-sm text-muted-foreground">
            Last failure: {new Date(status.last_failure_time).toLocaleString()}
          </div>
        )}

        {/* Reset Button */}
        {status.state !== 'closed' && (
          <Button 
            onClick={handleReset}
            variant="outline"
            className="w-full"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Circuit Breaker
          </Button>
        )}

        {/* Info */}
        <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded-lg">
          {status.state === 'open' && (
            <p>⚠️ Workflow is blocked due to excessive failures. Circuit will auto-retry after 60 seconds.</p>
          )}
          {status.state === 'half_open' && (
            <p>🔄 Testing if workflow has recovered. Next execution will determine final state.</p>
          )}
          {status.state === 'closed' && (
            <p>✅ Workflow is healthy. Circuit breaker will activate after 5 consecutive failures.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CircuitBreakerStatus;
